import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash';
import {
  currentMetricsMetaSelector,
  dimensionRowLimitSelector,
  realTimeModeOnTrendsSelector,
  getFirstAppliedFilter,
} from '@store/common/selectors';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import { filterModeAtom } from '@src/atoms/page';
import {
  formatColumns,
  formatAppData,
  formatMultiColumns,
  formatMultiData,
} from '../dimension-table/utils';
import { chartIntervalSelector, noBrushSelector } from '@store/trends/birdChart/selectors';
import {
  metricFilterRulesSelector,
  globalOrderBySelector,
  dimensionMetricsSelector,
} from '@store/trends/base/selectors';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { METRICS_MAPPING } from '@utils';
import { getPercentageMetric } from '@utils/metrics/metricHelpers';
import useFetchGroupBy from './useFetchGroupBy';
import useSetDimensionExport from './useSetDimensionExport';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';

export const useAppCommonQueryParams = () => {
  const metrics = useSelector(dimensionMetricsSelector) as string[];
  const filter = useSelector(getFirstAppliedFilter);
  const interval = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const selectedMetric = useSelector(selectedMetricSelector);
  const sortByMetric =
    useSelector(globalOrderBySelector) || METRICS_MAPPING[selectedMetric] || selectedMetric;
  const { limit } = useSelector(dimensionRowLimitSelector);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const noBrushSelected = useSelector(noBrushSelector);
  const { dataScope, usecaseKey } = useSelector(activeDashboardSelector);

  return {
    metrics,
    filter,
    interval,
    metricFilter,
    sortByMetric,
    limit,
    selectedMetric,
    realTimeOnTrends,
    noBrushSelected,
    dataScope,
    usecaseKey,
  };
};

function useAppDimension({ dimension, unit, enabled = true }) {
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const selectedMetric = useSelector(selectedMetricSelector);

  const {
    metrics,
    filter,
    interval,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    dataScope,
    usecaseKey,
  } = useAppCommonQueryParams();

  const appFilterMode = useAtomValue(filterModeAtom);
  const dimensionByTypes: any = useSelector(dimensionByTypesSelector);

  const [sortBy, setSortBy] = useState({
    key: sortByMetric as string,
    order: 'desc' as 'desc' | 'asc',
  });
  const onColumnSort = ({ key, order }) => {
    setSortBy({ key, order });
  };
  const [searchText, onSearch] = useState('');

  // update local sort when selected or order by metric changes
  useEffect(() => {
    onColumnSort({ key: sortByMetric, order: 'desc' });
  }, [sortByMetric, selectedMetric]);

  const {
    isFetching,
    isError,
    data = {},
    refetch,
  } = useFetchGroupBy({
    dimension,
    metrics,
    filter,
    interval,
    enabled,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    dataScope,
    usecaseKey,
  });

  const { adjustedInterval }: any = data;

  const { rows, totals, columns, pctShareMaxValues } = useMemo(() => {
    let rows = [];
    let totals = {};
    let columns = [];
    let pctShareMaxValues = {};

    // unit based metrics
    const columnMetrics = metrics.map((metric) => {
      return unit === '%' ? getPercentageMetric(metric) : metric;
    });

    const currentDimensionType = dimensionByTypes[dimension];

    if (!isEmpty(dimensionByTypes)) {
      if ('metricFilterData' in data) {
        ({ rows, totals, pctShareMaxValues } = formatMultiData({
          data,
          sortBy,
          searchText,
          metrics: columnMetrics,
        }));
        columns = formatMultiColumns({
          dimension,
          metrics: columnMetrics,
          showNAForMetric: null,
          realTimeOnTrends,
        });
      } else {
        ({ rows, totals, pctShareMaxValues } = formatAppData({
          data,
          sortBy,
          appFilterMode,
          searchText,
          columnMetrics,
          currentDimensionType,
        }));
        columns = formatColumns({
          dimension,
          metrics: columnMetrics,
          showNAForMetric: null,
          realTimeOnTrends,
        });
      }
    }

    return { rows, totals, columns, pctShareMaxValues };
  }, [
    appFilterMode,
    data,
    dimension,
    dimensionByTypes,
    metrics,
    currentMetricsMeta,
    realTimeOnTrends,
    searchText,
    sortBy,
    unit,
  ]);

  useSetDimensionExport({
    dimension,
    metrics,
    data: rows,
    totals: totals,
    showNAForMetric: null,
  });

  return {
    isFetching,
    isError,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals,
    data: rows,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    adjustedInterval,
    pctShareMaxValues,
    metrics,
  };
}

export default useAppDimension;
